'use client';

import { useState, useRef, Dispatch, SetStateAction, useEffect } from 'react';
import { Camera, Upload, FileText, X, Loader2, Sparkles } from 'lucide-react';
import { GetTokenCost } from '@/lib/tokensHandlers';
import HttpStatus from '@/lib/HttpStatus';
import Image from 'next/image';
import CartaTokens from '@/../public/images/tokens_sm.png';

export default function CollectibleUploader({ file, setFile, handleAnalyze, isLoading }: {
    file: File | null, 
    setFile: Dispatch<SetStateAction<File | null>>,
    handleAnalyze: () => Promise<void>,
    isLoading: boolean
    }) {
  const [preview, setPreview] = useState<string | null>(null);
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [ tokensCost, setTokensCost ] = useState<number>(0);
  const [ isLoadingCost, setIsLoadingCost ] = useState<boolean>(true);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
      async function getTokensCost() {
        setIsLoadingCost(true);

        try {
            const costResp = await GetTokenCost('COLCHK01');
    
            if(costResp.status != HttpStatus.OK) {
            throw new Error(costResp.error!);
            }
            
            setTokensCost(costResp.data);
        } catch(error) {
            setTokensCost(0);
        }
        setIsLoadingCost(false);
      }

      getTokensCost();      
  
  }, []);

  // Handle standard file uploads (Images + PDFs)
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    setFile(selectedFile);

    // If it's an image, create a local preview URL
    if (selectedFile.type.startsWith('image/')) {
      setPreview(URL.createObjectURL(selectedFile));
    } else if (selectedFile.type === 'application/pdf') {
      setPreview('pdf'); // Handle PDF icon placeholder
    }
  };

  // Trigger Live Desktop Webcam
  const startDesktopCamera = async () => {
    setIsCameraActive(true);
    setPreview(null);
    setFile(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment' } // Uses back camera on mobile if triggered here
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      console.error("Error accessing camera:", err);
      setIsCameraActive(false);
    }
  };

  // Capture Photo from Desktop Webcam Stream
  const capturePhoto = () => {
    if (!videoRef.current) return;
    
    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((blob) => {
        if (blob) {
          const capturedFile = new File([blob], "camera_capture.jpg", { type: "image/jpeg" });
          setFile(capturedFile);
          setPreview(canvas.toDataURL('image/jpeg'));
          stopCamera();
        }
      }, 'image/jpeg');
    }
  };

  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject as MediaStream;
      stream.getTracks().forEach(track => track.stop());
    }
    setIsCameraActive(false);
  };

  const clearSelection = () => {
    setFile(null);
    setPreview(null);
    stopCamera();
  };

  return (
    <div className="w-full mx-auto p-6 bg-card rounded-xl border shadow-sm">
      <h2 className="text-xl font-semibold mb-4 text-center">Scan Your Collectable</h2>
      
      {/* Dynamic Viewport: Camera Live Stream vs Preview vs Upload Box */}
      <div className="relative h-72 w-full rounded-lg border-2 border-dashed border-muted-foreground/20 bg-muted/10 flex flex-col items-center justify-center overflow-hidden mb-4">
        
        {isCameraActive ? (
          <div className="w-full h-full relative">
            <video ref={videoRef} autoPlay playsInline className="w-full h-full object-cover" />
            <button onClick={capturePhoto} className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground px-4 py-2 rounded-full font-medium shadow-md hover:bg-primary/90">
              Snap Photo
            </button>
          </div>
        ) : preview ? (
          <div className="w-full h-full flex items-center justify-center p-4">
            <button onClick={clearSelection} className="absolute top-2 right-2 p-1 bg-background/80 rounded-full border hover:bg-accent text-muted-foreground z-10">
              <X className="w-4 h-4" />
            </button>
            {preview === 'pdf' ? (
              <div className="text-center">
                <FileText className="w-16 h-16 text-primary mx-auto mb-2" />
                <span className="text-sm font-medium text-muted-foreground">{file?.name}</span>
              </div>
            ) : (
              <img src={preview} alt="Preview" className="w-full h-full object-contain rounded" />
            )}
          </div>
        ) : (
          // Default State: Instruction / Trigger Box
          <div className="text-center p-6 flex flex-col items-center gap-4">
            <Upload className="w-10 h-10 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              Drag & drop your file, or use the controls below
            </p>
          </div>
        )}
      </div>

      {/* Control Buttons */}
      {!isCameraActive && !file && (
        <div className="grid grid-cols-2 gap-3">
          {/* Native File Selector (Handles Mobile Camera Automatically via input config) */}
          <button 
            onClick={() => fileInputRef.current?.click()} 
            className="flex items-center justify-center gap-2 border p-3 rounded-lg hover:bg-accent font-medium text-sm transition"
          >
            <Upload className="w-4 h-4" /> Upload File
          </button>

          {/* Desktop Webcam Trigger */}
          <button 
            onClick={startDesktopCamera} 
            className="flex items-center justify-center gap-2 bg-secondary text-secondary-foreground p-3 rounded-lg hover:bg-secondary/80 font-medium text-sm transition"
          >
            <Camera className="w-4 h-4" /> Open Camera
          </button>
        </div>
      )}

      {/* Hidden Native File Input */}
      <input 
        type="file" 
        ref={fileInputRef} 
        onChange={handleFileChange} 
        accept="image/png, image/jpeg, image/jpg, application/pdf" 
        className="hidden" 
      />

      {file && (
        <button 
        onClick={handleAnalyze} 
        disabled={isLoading}
        className="w-full bg-primary text-primary-foreground p-3 rounded-lg font-medium hover:bg-primary/90 transition flex items-center justify-center gap-2 disabled:opacity-50 text-sm"
        >
        {isLoading ? (
            <>
            <Loader2 className="w-4 h-4 animate-spin" /> Analyzing Item Details...
            </>
        ) : (
            <div className="flex justify-center items-center gap-2">
              <div className="flex justify-center items-center">
                <Sparkles className="mr-2 w-4 h-4" /> 
                Analyze Collectible with AI
              </div>
              <div className="ml-2 flex justify-center items-center text-sm">
                <span>{"("}</span><Image src={CartaTokens} width={20} height={20} alt="Carta AI Tokens" />
                <span className="ml-1">
                  {
                    isLoadingCost 
                    ? <Loader2 size={32} className="animate-spin" /> 
                    : tokensCost
                  } 
                  <span > tokens</span>
                </span><span>{")"}</span> {/* className="hidden md:inline" */}
              </div>
            </div>
        )}
        </button>
    )}
    </div>
  );
}